import React, { useEffect, useState } from 'react';
import {
  Loader2, Quote, FileText, Building2, FileDown, Clock, AlertTriangle, FileSignature,
  ThumbsDown, HelpCircle, CheckCircle2, XCircle, CircleDashed, ClipboardCheck,
} from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { StrengthBadge, OppStatusBadge, ClassificationBadge, PricingBadge, DecisionBadge } from './OppBadges';
import { CalculationBuilder } from './CalculationBuilder';
import { ChangeOrderDialog } from './ChangeOrderDialog';
import { Loader } from '@/components/common/Feedback';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { fmtDate, fmtDateTime, SOURCE_LABEL, CLASSIFICATIONS, CLASSIFICATION_META, DECISIONS, DECISION_META } from '@/lib/format';
import { toast } from 'sonner';
import { track } from '@/lib/analytics';

const SOURCE_MEDIUM = { whatsapp: 'WhatsApp (written electronic communication)', email: 'Email', upload: 'Uploaded document', manual: 'Manual entry' };

// Parse a FastAPI error out of an axios blob/JSON error response.
const _blobErr = async (e) => {
  try {
    let d = e?.response?.data;
    if (d instanceof Blob) d = JSON.parse(await d.text());
    const det = d?.detail;
    if (typeof det === 'string') return det;
    if (det?.blockers) return `${det.message || 'Review conditions not met'}: ${det.blockers.join(' ')}`;
    if (det?.message) return det.message;
  } catch { /* ignore */ }
  return '';
};

const Section = ({ title, children, testid }) => (
  <section data-testid={testid}>
    <h4 className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">{title}</h4>
    {children}
  </section>
);

export const OpportunityDrawer = ({ opportunityId, open, onClose, onChanged }) => {
  const [opp, setOpp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [genPdf, setGenPdf] = useState(false);
  const [coOpen, setCoOpen] = useState(false);
  const [decisionReason, setDecisionReason] = useState('');
  const [ack, setAck] = useState(false);

  const load = () => {
    setLoading(true);
    return api.get(`/opportunities/${opportunityId}`).then(async (r) => {
      setOpp(r.data); setDecisionReason(r.data.decision_reason || '');
      // Inbox-style "mark as read": opening a New opportunity advances it to Reviewed.
      if (r.data.opportunity_status === 'new') {
        try {
          await api.patch(`/opportunities/${r.data.id}/status`, { status: 'reviewed' });
          setOpp((prev) => (prev ? { ...prev, opportunity_status: 'reviewed' } : prev));
          onChanged && onChanged();
        } catch { /* non-blocking */ }
      }
    }).finally(() => setLoading(false));
  };
  useEffect(() => { if (open && opportunityId) { setAck(false); load(); } else setOpp(null); /* eslint-disable-next-line */ }, [open, opportunityId]);

  const patchReview = async (patch, quiet) => {
    try {
      const r = await api.patch(`/opportunities/${opp.id}/review`, patch);
      setOpp(r.data);
      if (!quiet) toast.success('Saved');
      onChanged && onChanged();
      return r.data;
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); }
  };
  const saveDecision = async (decision) => {
    try {
      const r = await api.patch(`/opportunities/${opp.id}/decision`, { decision, decision_reason: decisionReason });
      setOpp(r.data);
      toast.success(`Decision: ${DECISION_META[decision]?.label}`);
      onChanged && onChanged();
    } catch (e) { toast.error('Could not save decision'); }
  };

  const generateNotice = async () => {
    setGenPdf(true);
    try {
      const r = await api.post(`/opportunities/${opp.id}/notice-pdf`,
        { acknowledged: true, deadline_acknowledged: true }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url;
      a.download = `notice-${(opp.title || 'opportunity').slice(0, 40).replace(/\s+/g, '-')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success('Notice PDF downloaded'); track('notice_generated', {});
    } catch (e) {
      const msg = await _blobErr(e);
      toast.error(msg || 'Could not generate the notice');
    }
    finally { setGenPdf(false); }
  };

  if (!open) return null;
  const calc = opp?.calculation || {};
  const status = opp?.pricing_status || 'unquantified';
  const trigger = (opp?.evidence || [])[0];
  const lines = calc.lines || [];
  const pricedLines = lines.filter((l) => l.priced);
  const tenderApplied = !!(calc.tender_adjustment && calc.tender_adjustment.applied);
  const hasEffectiveLines = lines.some((l) => (l.rate_basis || 'base') === 'effective');
  const incomplete = status !== 'fully_priced';
  // Pre-generation confirmation checklist (user-requested gate).
  const checks = opp ? [
    {
      key: 'classification',
      ok: !!opp.classification && opp.classification !== 'needs_review',
      label: opp.classification === 'needs_review'
        ? 'Classification is still "Needs review" — confirm it above before generating'
        : 'Classification reviewed',
    },
    {
      key: 'evidence',
      ok: !!trigger || (Array.isArray(opp.supporting_quotes) && opp.supporting_quotes.length > 0),
      label: (!!trigger || (opp.supporting_quotes || []).length > 0)
        ? 'Triggering evidence is visible'
        : 'No triggering communication or evidence linked',
    },
    {
      key: 'issue',
      ok: !!opp.notice_issue_date,
      label: opp.notice_issue_date ? 'Issue date confirmed' : 'Issue date not confirmed',
    },
    {
      key: 'qtyrate',
      ok: pricedLines.length > 0 && pricedLines.every((l) => l.rate_confirmed),
      warn: !(pricedLines.length > 0 && pricedLines.every((l) => l.rate_confirmed)),
      label: pricedLines.length === 0
        ? 'No priced line items yet — document will be labelled Unquantified'
        : pricedLines.every((l) => l.rate_confirmed)
          ? 'Quantities & rates confirmed where used'
          : 'Some priced lines have unconfirmed rates',
    },
    {
      key: 'tender',
      ok: true,
      label: tenderApplied
        ? `Tender applied to base-rate lines only${hasEffectiveLines ? ' (effective-rate lines excluded — no double count)' : ''}`
        : 'No tender adjustment applied (nothing to double-count)',
    },
    {
      key: 'deadline',
      ok: !!opp.notice_deadline_verified,
      warn: !opp.notice_deadline_verified,
      label: opp.notice_deadline_verified
        ? 'Deadline verified'
        : 'Deadline not verified — will be labelled "Not verified" on the document',
    },
  ] : [];
  const blocking = checks.some((c) => !c.ok && !c.warn);
  const canGenerate = ack && !blocking;

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto p-0" data-testid="opportunity-drawer">
        {loading || !opp ? (
          <div className="p-6"><Loader label="Loading opportunity..." /></div>
        ) : (
          <>
            <div className="bg-ink-900 text-white px-6 py-5">
              <SheetHeader><SheetTitle className="text-white text-lg leading-snug pr-6" data-testid="opp-drawer-title">{opp.title}</SheetTitle></SheetHeader>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <ClassificationBadge classification={opp.classification} testid="opp-drawer-classification" />
                <PricingBadge status={status} testid="opp-drawer-pricing" />
                <StrengthBadge strength={opp.strength} testid="opp-drawer-strength" />
                <DecisionBadge decision={opp.decision} testid="opp-drawer-decision" />
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-slate-300">
                <span className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" />{opp.project_name}</span>
                <span className="flex items-center gap-1.5" data-testid="opp-drawer-detected"><Clock className="h-3.5 w-3.5" />Detected: {fmtDateTime(opp.created_at)}</span>
                {opp.event_date && (
                  <span className="text-slate-400" data-testid="opp-drawer-event-date">Event date: {fmtDate(opp.event_date)}</span>
                )}
                {opp.notice_deadline_verified && opp.notice_days_left != null && (
                  <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold bg-amber-500/20 text-amber-200" data-testid="opp-notice-countdown">
                    <Clock className="h-3 w-3" />{opp.notice_days_left} day(s) to notice deadline
                  </span>
                )}
              </div>
            </div>

            <div className="px-6 py-5 space-y-6">
              {/* Triggering communication */}
              {trigger && (
                <Section title="Triggering communication" testid="opp-trigger">
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
                      <span><b>Type:</b> {SOURCE_MEDIUM[trigger.source_type] || SOURCE_LABEL[trigger.source_type] || trigger.source_type}</span>
                      <span><b>From:</b> {trigger.sender || '—'}</span>
                      <span><b>Date:</b> {fmtDate(trigger.timestamp)}</span>
                    </div>
                    {trigger.subject && <div className="text-xs text-slate-600 mt-1"><b>Subject:</b> {trigger.subject}</div>}
                    {trigger.content && <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap max-h-40 overflow-y-auto" data-testid="opp-trigger-content">{trigger.content}</p>}
                  </div>
                </Section>
              )}

              <Section title="What we found" testid="opp-summary-sec"><p className="text-sm text-slate-700 leading-relaxed" data-testid="opp-summary">{opp.summary || '—'}</p></Section>

              {opp.baseline_summary && <Section title="Original baseline (from contract)"><p className="text-sm text-slate-700" data-testid="opp-baseline">{opp.baseline_summary}</p></Section>}
              {opp.commercial_reasoning && <Section title="Why this may be relevant"><p className="text-sm text-slate-700" data-testid="opp-why">{opp.commercial_reasoning}</p></Section>}

              {/* Supporting evidence */}
              {Array.isArray(opp.supporting_quotes) && opp.supporting_quotes.length > 0 && (
                <Section title="Supporting evidence (quotes)" testid="opp-supporting">
                  <div className="space-y-2">
                    {opp.supporting_quotes.map((q, i) => (
                      <div key={i} className="rounded-lg border border-slate-200 bg-slate-50 p-2.5" data-testid={`opp-evidence-${i}`}>
                        <div className="flex gap-2"><Quote className="h-3.5 w-3.5 text-slate-400 shrink-0 mt-0.5" /><p className="text-sm text-slate-700 italic">&ldquo;{q.quote}&rdquo;</p></div>
                        {q.why && <p className="text-xs text-slate-500 mt-1 pl-5">{q.why}</p>}
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {/* Evidence against */}
              {Array.isArray(opp.evidence_against) && opp.evidence_against.length > 0 && (
                <Section title="Evidence against / weakens the case" testid="opp-against">
                  <div className="space-y-2">
                    {opp.evidence_against.map((q, i) => (
                      <div key={i} className="rounded-lg border border-amber-200 bg-amber-50 p-2.5" data-testid={`opp-against-${i}`}>
                        <div className="flex gap-2"><ThumbsDown className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" /><p className="text-sm text-amber-800">{q.quote || q}</p></div>
                        {q.why && <p className="text-xs text-amber-600 mt-1 pl-5">{q.why}</p>}
                      </div>
                    ))}
                  </div>
                  {opp.weakens_case && <p className="text-xs text-amber-700 mt-2">{opp.weakens_case}</p>}
                </Section>
              )}

              {/* Missing information */}
              {Array.isArray(opp.missing_information) && opp.missing_information.length > 0 && (
                <Section title="Missing information" testid="opp-missing">
                  <ul className="space-y-1">
                    {opp.missing_information.map((m, i) => (
                      <li key={i} className="flex gap-2 text-sm text-slate-600"><HelpCircle className="h-3.5 w-3.5 text-slate-400 mt-0.5 shrink-0" />{m}</li>
                    ))}
                  </ul>
                </Section>
              )}

              {/* Classification + decision */}
              <Section title="Classification & decision" testid="opp-review-controls">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] text-slate-500">Classification (human review)</label>
                    <Select value={opp.classification} onValueChange={(v) => patchReview({ classification: v })}>
                      <SelectTrigger className="h-9 mt-1" data-testid="opp-classification-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{CLASSIFICATIONS.map((c) => <SelectItem key={c} value={c}>{CLASSIFICATION_META[c].label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-[11px] text-slate-500">Decision</label>
                    <Select value={opp.decision || ''} onValueChange={saveDecision}>
                      <SelectTrigger className="h-9 mt-1" data-testid="opp-decision-select"><SelectValue placeholder="Choose…" /></SelectTrigger>
                      <SelectContent>{DECISIONS.map((d) => <SelectItem key={d} value={d}>{DECISION_META[d].label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <Textarea value={decisionReason} onChange={(e) => setDecisionReason(e.target.value)} onBlur={() => opp.decision && saveDecision(opp.decision)} placeholder="Reason for the decision (optional)" className="mt-2 text-sm" rows={2} data-testid="opp-decision-reason" />
              </Section>

              {/* Dates */}
              <Section title="Dates & notice deadline" testid="opp-dates">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="text-[11px] text-slate-500">Event date</label>
                    <Input type="date" defaultValue={(opp.event_date || '').slice(0, 10)} onBlur={(e) => patchReview({ event_date: e.target.value }, true)} className="h-9 mt-1" data-testid="opp-event-date" />
                  </div>
                  <div>
                    <label className="text-[11px] text-slate-500">Notice issue date</label>
                    <Input type="date" defaultValue={(opp.notice_issue_date || '').slice(0, 10)} onBlur={(e) => patchReview({ notice_issue_date: e.target.value }, true)} className="h-9 mt-1" data-testid="opp-issue-date" />
                  </div>
                  <div>
                    <label className="text-[11px] text-slate-500">Notice period (days)</label>
                    <Input type="number" defaultValue={opp.notice_period_days ?? ''} onBlur={(e) => patchReview({ notice_period_days: e.target.value ? Number(e.target.value) : null }, true)} className="h-9 mt-1" placeholder="e.g. 14" data-testid="opp-period-days" />
                    <p className="text-[10px] text-slate-400 mt-1">Project setting — not a contractual period. Verify against your contract.</p>
                  </div>
                </div>
                <div className="mt-2 rounded-lg border border-slate-200 p-2.5" data-testid="opp-deadline">
                  {opp.notice_deadline_verified ? (
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm text-money-700 font-medium">Deadline confirmed: {(opp.notice_deadline || '').slice(0, 10)}</span>
                      <Button variant="ghost" size="sm" className="h-7 text-xs text-slate-500"
                        onClick={() => patchReview({ notice_deadline_verified: false }, true)} data-testid="opp-unconfirm-deadline">Unconfirm</Button>
                    </div>
                  ) : (opp.notice_issue_date && opp.notice_period_days) ? (
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm text-amber-700">Suggested deadline: <b>{(opp.notice_deadline || opp.notice_deadline_suggested || '').slice(0, 10)}</b> — not yet confirmed.</span>
                      <Button size="sm" className="h-7 text-xs bg-money hover:bg-money-700"
                        onClick={() => patchReview({ notice_deadline: (opp.notice_deadline || opp.notice_deadline_suggested || '').slice(0, 10), notice_deadline_verified: true }, true)}
                        data-testid="opp-confirm-deadline">Confirm deadline</Button>
                    </div>
                  ) : (
                    <span className="text-sm text-amber-600 font-medium">Notice deadline not verified — set an issue date and the project notice period, then confirm.</span>
                  )}
                </div>
              </Section>

              {/* Calculation */}
              <CalculationBuilder opportunityId={opp.id} calculation={calc} currency={calc.currency || 'USD'} onSaved={(updated) => setOpp(updated)} />
            </div>

            {/* Actions + validation */}
            <div className="px-6 py-4 border-t border-slate-100 sticky bottom-0 bg-white space-y-3">
              {incomplete && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 flex gap-2" data-testid="incomplete-pricing-warning">
                  <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                  <div className="text-xs text-amber-800">
                    <div className="font-semibold flex items-center gap-1.5">
                      This opportunity is <PricingBadge status={status} testid="warn-pricing-badge" />
                    </div>
                    <p className="mt-1 leading-relaxed">
                      The document will be clearly labelled as <b>{status === 'partially_priced' ? 'Partially priced' : 'Unquantified'}</b>.
                      No partial figure will be presented as the final claim amount — incomplete items are marked
                      &ldquo;<b>financial impact to be substantiated</b>&rdquo;.
                    </p>
                  </div>
                </div>
              )}

              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3" data-testid="pdf-validation">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5 flex items-center gap-1.5"><ClipboardCheck className="h-3.5 w-3.5" />Before generating — commercial review</div>
                <ul className="space-y-1">
                  {checks.map((c) => (
                    <li key={c.key} className="flex items-center gap-2 text-xs" data-testid={`check-${c.key}`}>
                      {c.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-money-600" /> : c.warn ? <AlertTriangle className="h-3.5 w-3.5 text-amber-500" /> : <XCircle className="h-3.5 w-3.5 text-danger-600" />}
                      <span className={cn(c.ok ? 'text-slate-600' : c.warn ? 'text-amber-700' : 'text-danger-600')}>{c.label}</span>
                    </li>
                  ))}
                </ul>
                <label className="mt-3 flex items-start gap-2 text-xs text-slate-700 cursor-pointer" data-testid="gen-ack-wrap">
                  <Checkbox checked={ack} onCheckedChange={(v) => setAck(!!v)} disabled={blocking} data-testid="gen-ack" className="mt-0.5" />
                  <span>I have reviewed the classification, evidence, quantities/rates, tender treatment and dates above, and want to generate this document.</span>
                </label>
                {blocking && <p className="mt-2 text-[11px] text-danger-600" data-testid="gen-blocked-msg">Resolve the items marked in red before generating.</p>}
              </div>

              <div className="grid grid-cols-2 gap-2">
                <Button onClick={generateNotice} disabled={genPdf || !canGenerate} className="bg-primary hover:bg-ink-700" data-testid="generate-notice-btn">
                  {genPdf ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}Notice PDF
                </Button>
                <Button onClick={() => setCoOpen(true)} disabled={!canGenerate} variant="outline" className="border-money/30 text-money-700 hover:bg-money-50" data-testid="generate-co-btn">
                  <FileSignature className="h-4 w-4 mr-2" />Change Order
                </Button>
              </div>
              <p className="text-[11px] text-slate-400 text-center">Amounts come only from the calculation above — never an AI guess. Notice & CO use the same numbers.</p>
            </div>
          </>
        )}
      </SheetContent>
      <ChangeOrderDialog opportunityId={opp?.id} open={coOpen} onClose={() => setCoOpen(false)} acknowledged={ack} />
    </Sheet>
  );
};
